<template>
  <div class='cm-checkout'>
    <p v-if="error" class="message message-error ss-alert">{{ error.message }}</p>

    <div class="guild guild--with-columns guild--borderless">
      <div class="guild__column">
        <div class="plan-card">
          <div class="plan-card__inner">
            <div class="plan-card__plan">
              {{ I18n.t('creativeguild.pricing.your_cycle_plan_based_on_company_size', {cycle: I18n.t(billing_period)}) }}
            </div>
            <div class="plan-card__frequency">
              <span class="plan-card__frequency-notice">
                <button
                  class="plan-card__period-toggle"
                  type="button"
                  @click="billing_period = nextBillingPeriod"
                >
                  {{ I18n.t(`creativeguild.pricing.switch_to_${nextBillingPeriod}`) }}
                </button>
                <span v-if="billing_period === 'monthly'" class="plan-card__frequency-savings">
                  , {{ I18n.t('creativeguild.checkout.save_num', {num: formatMoney(monthlySubTotal * 2)}) }}
                </span>
              </span>
            </div>

            <CmMarkdown :input="I18n.t(`creativeguild.pricing.tier_${plan.value}_body`)" />

            <footer>
              <table class="plan-card__price-table">
                <tr v-if="discountApplied || isYearly">
                  <td>{{ I18n.t(`creativeguild.checkout.subtotal`) }}</td>
                  <td class="plan-card__price plan-card__price--subtotal">
                    {{ formatMoney(subTotal) }}
                  </td>
                </tr>
                <tr v-if="isYearly">
                  <td>{{ I18n.t(`creativeguild.checkout.yearly_savings`) }}</td>
                  <td class="plan-card__price plan-card__price--subtotal">
                    {{ formatMoney(monthlySubTotal * -2) }}
                  </td>
                </tr>
                <tr
                  v-if="discountApplied"
                  class="plan-card__price-row"
                  :class="{'plan-card__price-row--is-loading': discountLoading}"
                >
                  <td>
                    {{ I18n.t(`creativeguild.checkout.discount`) }}
                    <button
                      class="plan-card__current-discount-button"
                      title="Remove"
                      @click="discount_code = ''"
                    >
                      ({{ discount_code.toUpperCase() }})
                    </button>
                  </td>
                  <td class="plan-card__price plan-card__price--discount">
                    {{ formatMoney(totalDiscount * -1) }}
                  </td>
                </tr>
                <tr>
                  <td>{{ I18n.t(`creativeguild.checkout.total`) }}</td>
                  <td class="plan-card__price plan-card__price--total">
                    {{ formatMoney(total) }}
                  </td>
                </tr>
              </table>
            </footer>
          </div>
        </div>
        <div class="checkout-disclaimer">
          <div class="secure ss-lock">
            {{ I18n.t('this_is_a_secure_transaction') }}
          </div>
          <small class="terms">
            <CmMarkdown
              :input="I18n.t('creativeguild.checkout.terms', {
                period: `<span class='checkout-disclaimer__period'>${billing_period.replace('ly', '')}</span>`,
                amount: `<span class='checkout-disclaimer__price'>${formatMoney(total)}</span>`
              })"
            />
          </small>
        </div>
      </div>

      <div class="guild__column">
        <div id="dropin-container" />
        <div class="field">
          <label
            class="cm-label"
            for="discount_code_unapplied"
          >{{I18n.t("activerecord.attributes.company.discount_code")}}</label>
          <div class="discount-field-container">
            <input
              class="cm-field"
              id="discount_code_unapplied"
              type="text"
              v-model="discount_code_unapplied"
              @keydown.enter.prevent="discount_code = discount_code_unapplied"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
            />
            <button
              class='cm-button'
              :class='{
                "button--success": discountApplied && !discountLoading && discountUnchanged,
                "button--failure": discountInvalid && !discountLoading && discountUnchanged
              }'
              :disabled='(discountApplied || discountInvalid) && discountUnchanged'
              type="button"
              @click="discount_code = discount_code_unapplied"
            >
              {{ applyButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <input type="hidden" name="company[plan]" :value="plan.value" />
    <input type="hidden" name="company[billing_period]" :value="billing_period" />
    <input type="hidden" name="company[discount_code]" :value="discount && discount.name">
    <input type="hidden" name="company[nonce]" ref="nonceInput" />

    <input
      type="submit"
      :value="I18n.t('creativeguild.checkout.button')"
      class="cm-button guild__button--continue"
    />
  </div>
</template>

<script>
import CmMarkdown from "./markdown";
import CmSelect from "./select";
export default {
  components: {
    CmMarkdown,
    CmSelect
  },
  props: {
    plan: {
      type: Object
    },
    initialBillingPeriod: {
      type: String
    },
    braintreeLocale: {
      type: String
    },
    braintreeClientToken: {
      type: String
    }
  },

  data() {
    return {
      discount_code_unapplied: "",
      discount_code: "",
      billing_period: this.initialBillingPeriod,

      discountLoading: false,

      discountResponse: null,
      error: null
    };
  },

  mounted() {
    var form = document.querySelector("form.edit_company");
    var button = form.querySelector("input[type=submit]");

    const _this = this;

    braintree.dropin.create(
      {
        authorization: _this.braintreeClientToken,
        container: "#dropin-container",
        paypal: { flow: "vault" },
        locale: _this.braintreeLocale
      },
      function(createErr, instance) {
        button.addEventListener("click", function(e) {
          e.preventDefault();

          instance.requestPaymentMethod(function(
            requestPaymentMethodErr,
            payload
          ) {
            if (requestPaymentMethodErr) {
              _this.error = requestPaymentMethodErr
              // An appropriate error will be shown in the UI
            } else {
              // Submit payload.nonce to your server
              _this.$refs.nonceInput.value = payload.nonce;
              form.submit();
            }
          });
        });
      }
    );
  },

  computed: {
    isYearly() {
      return this.billing_period === "yearly";
    },

    monthlySubTotal() {
      return this.plan.price;
    },

    subTotal() {
      return this.monthlySubTotal * (this.isYearly ? 12 : 1);
    },

    subTotalWithYearlySavings() {
      if (this.isYearly) {
        return this.subTotal - this.subTotal * 2 / 12;
      }
      return this.subTotal;
    },

    totalDiscount() {
      const billingCycleCount =
        (this.discount.number_of_billing_cycles || 12) /
        (this.isYearly ? 12 : 1);

      let amount = Math.min(
        this.subTotal / (this.isYearly ? 12 : 1),
        this.discount.amount
      );
      if (this.isYearly) amount *= 10;
      if (billingCycleCount < 1) amount *= billingCycleCount;

      return amount;
    },

    total() {
      if (this.discount) {
        return Math.max(this.subTotalWithYearlySavings - this.totalDiscount, 0);
      } else {
        return this.subTotalWithYearlySavings;
      }
    },

    discountApplied() {
      return this.discountResponse && this.discountResponse.success;
    },

    discountInvalid() {
      return this.discountResponse && this.discountResponse.success === false;
    },

    discountUnchanged() {
      return (
        this.discount_code_unapplied.toLowerCase() ===
        this.discount_code.toLowerCase()
      );
    },

    discount() {
      if (this.discountApplied) {
        const item = this.discountResponse.item;
        return {
          ...item,
          amount: parseFloat(item.amount)
        };
      }
    },

    nextBillingPeriod() {
      return this.isYearly ? "monthly" : "yearly";
    },

    applyButtonLabel() {
      if (this.discountLoading) return "...";

      if (this.discountUnchanged) {
        if (this.discountApplied) return this.I18n.t('creativeguild.checkout.discount_code_applied');
        if (this.discountInvalid) return this.I18n.t('creativeguild.checkout.discount_code_invalid');
      }

      return this.I18n.t('creativeguild.checkout.apply_discount');
    }
  },

  watch: {
    plan(next, prev) {
      if (this.discount_code === "") {
        this.discountResponse = null;
      } else {
        this.checkDiscount();
      }
    },

    discount_code(next, prev) {
      if (this.discount_code === "") {
        this.discountResponse = null;
      } else {
        this.checkDiscount();
      }
    }
  },

  methods: {
    checkDiscount() {
      this.discountLoading = true;
      this.$http
        .get(`/discounts/${this.plan.value}/${this.discount_code}`)
        .then(response => {
          this.discountResponse = response.body;
          this.discountLoading = false;
        });
    },

    formatMoney(value) {
      return this.I18n.toCurrency(value, { unit: '$' })
    }
  }
};
</script>

<style lang='scss' scoped>
.cm-checkout {
}
</style>



// WEBPACK FOOTER //
// app/javascript/components/checkout.vue?390bc55c